import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  useRef,
  memo,
  type JSX,
} from 'react';
import debounce from 'debounce';
import AttributeValue, {
  AttributeValueSizeType,
} from '../AttributeValue/AttributeValue';
import {
  ValueType,
  ParseResult,
  IParseResult,
  IFormatter,
} from '../../../Utils/Formatters/FormatterTypes';
import { findFormatter } from '../../../Utils/Formatters/FindFormatter';
import {
  IClientAttributeModel,
  IClientBusinessModel,
} from '../../../Types/ClientModelTypes';
import EDDatePicker from '../../../BaseComponents/DatePicker/EDDatePicker';
import {
  EDAutoCompleteEdit,
  ISuggestion,
} from '../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { IFieldValidations } from '../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { GridContext } from '../../../Features/GridContext';
import { DEBOUNCE_DELAY_TIME } from '../../../Const/SystemWideConsts';
import { businessDataFormValuesEqual } from '../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataFormHasChanged/businessDataFormHasChanged';
import { IEditComponentProps } from './EditComponentTypes';
import EDCheckbox from '../../../BaseComponents/Checkboxes/EDCheckbox';
import { EDTextEdit } from '../../../BaseComponents/TextEdit/EDTextEdit';
import { SpacingProps } from '@mui/system';
import {
  isParseError,
  isParseWarning,
} from '../../../Utils/Formatters/Utils/ValueUtils';
import { EDTimePicker } from '../../../BaseComponents/TimePicker/EDTimePicker';
import { useFullscreenView } from '../../../Hooks/useFullscreenView';

interface IBaseProps extends SpacingProps {
  value: ValueType;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
  editMode: boolean;
  endAdornment?: JSX.Element;
  collection: IClientBusinessModel;
  attribute: IClientAttributeModel;
  size?: AttributeValueSizeType;
  autoFocus?: boolean;
  disableAnimation?: boolean;
  fullWidth?: boolean;
  isInList?: boolean;
  header?: boolean;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  requiredMessage?: string;
  editCancelled?: boolean;
  disablePaddingRight?: boolean;
  disableRelativeSize?: boolean;
  minRows?: number;
  maxRows?: number;
  multiline?: boolean;
  valueNewLine?: boolean;
  helperText?: string;
  tooltip?: string;
  errorMessage?: string;
  warningMessage?: string;
  hintMessage?: string;
  onlyValue?: boolean;
  hideOutline?: boolean;
  hideClearIcon?: boolean;
  defaultClearValue?: string;
  rightAlign?: boolean;
  disableInputPaddingRight?: boolean;
  rightPadding?: number;
  lineMarker?: boolean;
  disableDisabledBackgroundColor?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
  dataLoading?: boolean;
  testId?: string;
  disableTabbing?: Boolean;
  grayTextColor?: boolean;
  tooltipRensa?: string;
  savedValue?: string;
  disableChangeMarker?: boolean;
  /** Beräkna internt om ändringsmarkeringen ska visas när kontrollen är focused */
  calcFocusedChangeMarker?: boolean;
  disableFutureDates?: boolean;
  formControlMargin?: 'none' | 'dense' | 'normal';
  maxInputLength?: number;
  onBeforeClear?: () => void;
  editComponent?: React.ComponentType<
    React.PropsWithChildren<IEditComponentProps<any>>
  >;
  /** Värden som går att välja, hämtas default från EnumVvalues i modellen */
  options?: ISuggestion[];
  /** Om attributets värde är maskerat och ska visas som "Dold uppgift" */
  masked?: boolean;
}

interface IProps extends IBaseProps {
  onBlur?: () => void;
  onChange: (newValue: any, validationErrorMessage?: IFieldValidations) => void;
  marker?: boolean;
  /** Debounce time in millisecond, set to 0 to disable debounce */
  debounceDelayTime?: number;
}

interface IInnerProps extends IBaseProps {
  baseValue: ValueType;
  value: string;
  edited: boolean;
  formatter: IFormatter;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  baseOnChange: (newValue: ValueType) => void;
  onChange: (newValue: string) => void;
  onEventChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onEnumChange: (suggestion: ISuggestion | null) => void;
  onReset: (event: React.MouseEvent) => void;
  onBeforeInput: (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  focused: boolean;
  validationResult?: IParseResult;
  errorMessage: string;
  inputRef: React.MutableRefObject<HTMLInputElement | undefined>;
}

interface IChangeData {
  value: string;
  errorMessage: string;
  parseResult: IParseResult;
  validationResult?: IParseResult;
  validationErrorMessage?: IFieldValidations;
}

export const editcomponentDefaultPadding = '0px';
export const editcomponentDefaultFullWidthPadding = '0px';

const EditableAttribute: React.FC<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const gridContext = useContext(GridContext);
  const options: ISuggestion[] | undefined = useMemo(() => {
    if (props.options !== undefined) {
      return props.options;
    } else if (
      props.attribute.DataType === 'Enum' &&
      props.attribute.EnumValues !== undefined
    ) {
      return Array.from(props.attribute.EnumValues, ([key, value]) => ({
        name: value,
        value: key,
      }));
    } else {
      return undefined;
    }
  }, [props.attribute.DataType, props.attribute.EnumValues, props.options]);

  if (props.editMode) {
    const label = props.label ?? props.attribute.DisplayLabel;
    const EditComp: React.ComponentType<
      React.PropsWithChildren<IEditComponentProps<any>>
    > =
      props.editComponent ??
      (options !== undefined
        ? EDAutoCompleteEdit
        : props.attribute.DataType === 'Date' ||
          props.attribute.DataType === 'DateTime'
        ? EDDatePicker
        : props.attribute.DataType === 'Time'
        ? EDTimePicker
        : props.attribute.DataType === 'Boolean'
        ? EDCheckbox
        : EDTextEdit);
    return (
      <>
        {EditComp ? (
          <EditComp
            grayTextColor={props.grayTextColor}
            tooltipRensa={props.tooltipRensa}
            onFocus={props.onFocus}
            id={props.attribute.AttributeName}
            onKeyDown={props.onKeyDown}
            eventOnChange={props.onEventChange}
            onChange={props.onChange}
            noChangeOnBlur
            baseOnChange={props.baseOnChange}
            onReset={props.onReset}
            baseValue={props.baseValue}
            value={props.value}
            label={label}
            onBlur={props.onBlur}
            autoFocus={props.autoFocus}
            disableAnimation={props.disableAnimation}
            disabled={!!props.disabled}
            multiline={props.multiline}
            minRows={props.minRows}
            maxRows={props.maxRows}
            endAdornment={props.endAdornment}
            fullWidth={!!props.fullWidth}
            relativeSize={!props.disableRelativeSize}
            required={props.required}
            requiredMessage={props.requiredMessage}
            error={
              !props.focused &&
              (isParseError(props.validationResult?.result) ||
                Boolean(props.errorMessage))
            }
            warning={
              !props.focused &&
              (isParseWarning(props.validationResult?.result) ||
                Boolean(props.warningMessage))
            }
            hint={Boolean(props.hintMessage)}
            helperText={
              !props.focused
                ? props.errorMessage
                  ? props.errorMessage
                  : props.warningMessage
                  ? props.warningMessage
                  : props.hintMessage
                : undefined
            }
            marker={props.edited}
            inputProps={{
              onBeforeInput: props.onBeforeInput,
              tabIndex: props.disableTabbing ? -1 : undefined,
            }}
            size={gridContext.inGrid ? 'ownblock' : props.size}
            inputRef={props.inputRef}
            tooltip={props.tooltip}
            autoComplete="off"
            allMargins={props.formControlMargin}
            smallScreenSize={'100%'}
            testId={props.testId ?? props.attribute.AttributeName}
            hideLabel={props.onlyValue}
            hideOutline={props.hideOutline}
            hideClearIcon={props.hideClearIcon}
            rightAlign={props.rightAlign}
            disableInputPaddingRight={props.disableInputPaddingRight}
            rightPadding={props.rightPadding}
            lineMarker={props.lineMarker}
            disableDisabledBackgroundColor={
              props.disableDisabledBackgroundColor
            }
            options={options}
            disableFutureDates={props.disableFutureDates}
            paddingX={props.paddingX}
            paddingY={props.paddingY}
            paddingRight={props.paddingRight}
            paddingLeft={props.paddingLeft}
            masked={props.masked}
          />
        ) : (
          <h1>Oväntat</h1>
        )}
      </>
    );
  } else {
    return (
      <AttributeValue
        collection={props.collection}
        attribute={props.attribute}
        value={props.baseValue}
        label={props.label}
        size={props.size}
        header={props.header}
        onClick={props.onClick}
        valueNewLine={props.valueNewLine}
        testId={props.testId ?? props.attribute.AttributeName}
        hideLabel={props.onlyValue}
        masked={props.masked}
      />
    );
  }
};

const EnhancedEditableAttribute: React.FC<React.PropsWithChildren<IProps>> =
  memo(props => {
    let {
      onFocus: baseOnFocus,
      onBlur: baseOnBlur,
      value: baseValue,
      onChange: baseOnChange,
      attribute,
      marker,
      autoFocus: baseAutoFocus,
      ...rest
    } = props;
    const [errorMessage, setErrorMessage] = useState('');
    const [validationResult, setValidationResult] = useState<IParseResult>();
    const localInputRef = useRef<HTMLInputElement>(undefined);
    const inputRef = props.inputRef || localInputRef;
    const fullScreenEditView = useFullscreenView();
    const autoFocus = !!baseAutoFocus && !fullScreenEditView;

    const formatter = useMemo(
      () => findFormatter(props.attribute.Formatter, props.attribute.DataType),
      [props.attribute]
    );

    const [value, setValue] = useState(
      formatter.displayer.display(
        baseValue,
        true,
        props.attribute.FormatterDisplayParams
      )
    );
    const [focused, setFocused] = useState(autoFocus);
    useEffect(() => {
      setErrorMessage('');
      setValidationResult(undefined);
    }, [props.editCancelled]);
    useEffect(() => {
      if (
        !focused &&
        (!validationResult || !isParseError(validationResult.result))
      ) {
        setValue(
          formatter.displayer.display(
            baseValue,
            true,
            props.attribute.FormatterDisplayParams
          )
        );
      }
    }, [
      baseValue,
      focused,
      formatter,
      validationResult,
      props.attribute.FormatterDisplayParams,
    ]);

    useEffect(() => {
      if (!props.dataLoading) {
        setValue(
          formatter.displayer.display(
            baseValue,
            true,
            props.attribute.FormatterDisplayParams
          )
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.dataLoading]);

    const getChanges = useCallback(
      (newValue: string): IChangeData => {
        const parseResult = formatter.parser.parse(
          newValue,
          props.attribute.FormatterParseParams
        );
        if (parseResult.result === ParseResult.OK) {
          const newValidateResult = formatter.validator.validate(
            parseResult.value,
            props.attribute.FormatterValidateParams
          );
          let validationErrorMessage = '';

          if (newValidateResult.result !== ParseResult.OK) {
            validationErrorMessage = formatter.validator.errorMessage(
              newValue,
              newValidateResult,
              formatter.displayer,
              props.attribute.FormatterValidateParams,
              props.attribute.FormatterDisplayParams,
              props.attribute.DisplayLabel,
              parseResult.preFix
            );
            setErrorMessage(validationErrorMessage);
          } else {
            setErrorMessage('');
          }

          setValidationResult(newValidateResult);
          const formValidation: IFieldValidations | undefined =
            newValidateResult.result !== ParseResult.OK
              ? [
                  {
                    fieldName: props.attribute.AttributeName,
                    message: validationErrorMessage,
                    type: isParseWarning(newValidateResult.result)
                      ? 'Warning'
                      : 'Error',
                  },
                ]
              : undefined;
          return {
            value: newValue,
            parseResult,
            errorMessage: validationErrorMessage,
            validationResult: newValidateResult,
            validationErrorMessage: formValidation,
          };
        } else {
          const errorMessage = formatter.parser.errorMessage(
            newValue,
            parseResult,
            props.attribute.FormatterParseParams,
            props.attribute.DisplayLabel
          );
          const formValidation: IFieldValidations = [
            {
              fieldName: props.attribute.AttributeName,
              message: errorMessage,
              type: isParseWarning(parseResult.result) ? 'Warning' : 'Error',
            },
          ];
          return {
            value: newValue,
            parseResult,
            errorMessage,
            validationErrorMessage: formValidation,
          };
        }
      },
      [formatter, props.attribute]
    );

    const onFocus = useCallback(
      (event: React.FocusEvent<HTMLElement>) => {
        setFocused(true);
        if (baseOnFocus) {
          baseOnFocus(event);
        }
      },
      [setFocused, baseOnFocus]
    );
    const onBlur = useCallback(() => {
      setFocused(false);
      const changes = getChanges(value);
      if (changes.parseResult.result === ParseResult.OK) {
        const displayText = formatter.displayer.display(
          changes.parseResult.value,
          true,
          attribute.FormatterDisplayParams
        );
        if (baseOnBlur) {
          baseOnBlur();
        }
        setValue(displayText);
      }
    }, [
      getChanges,
      value,
      formatter.displayer,
      attribute.FormatterDisplayParams,
      baseOnBlur,
    ]);

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (
          !formatter.parser.validateKey(
            event,
            props.attribute.FormatterParseParams
          )
        ) {
          event.preventDefault();
          const element = event.currentTarget;
          const selectionStart = element.selectionStart;
          const selectionEnd = element.selectionEnd;
          window.requestAnimationFrame(() => {
            if (
              element &&
              element.selectionStart &&
              element.selectionEnd &&
              selectionStart &&
              selectionEnd
            ) {
              element.selectionStart = selectionStart;
              element.selectionEnd = selectionEnd;
            }
          });
        }
      },
      [formatter.parser, props.attribute.FormatterParseParams]
    );

    const debouncedOnChange = useMemo(
      () =>
        debounce(
          props.onChange,
          props.debounceDelayTime ?? DEBOUNCE_DELAY_TIME
        ),
      [props.onChange, props.debounceDelayTime]
    );

    const propsOnChange =
      props.debounceDelayTime === 0 ||
      props.attribute.DataType === 'Date' || //Hoppa över för datumfält. Debounce skapar problem när man väljer datum från kalendern.
      props.attribute.DataType === 'DateTime'
        ? props.onChange
        : debouncedOnChange;

    const onEventChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const rawValue = event.target.value;
        // Filter invalid characters for mobile compatibility
        const filteredValue = formatter.parser.filterInvalidCharacters(
          rawValue,
          props.attribute.FormatterParseParams
        );
        const newValue = filteredValue.substring(
          0,
          props.maxInputLength ?? props.attribute.MaxInputLength
        );
        const changes = getChanges(newValue);
        setValue(newValue);
        if (changes.parseResult.result === ParseResult.OK) {
          propsOnChange(
            changes.parseResult.value,
            changes.validationErrorMessage
          );
        } else {
          propsOnChange(props.value, changes.validationErrorMessage);
        }
      },
      [
        props.maxInputLength,
        props.attribute.MaxInputLength,
        props.value,
        getChanges,
        propsOnChange,
        formatter.parser,
        props.attribute.FormatterParseParams,
      ]
    );

    const onChange = useCallback(
      (newValue: unknown) => {
        if (typeof newValue === 'string') {
          const filteredValue = formatter.parser.filterInvalidCharacters(
            newValue,
            props.attribute.FormatterParseParams
          );
          const cappedValue = filteredValue.substring(
            0,
            props.maxInputLength ?? props.attribute.MaxInputLength
          );
          const changes = getChanges(cappedValue);
          if (changes.parseResult.result === ParseResult.OK) {
            setValue(cappedValue);
            propsOnChange(
              changes.parseResult.value,
              changes.validationErrorMessage
            );
          }
        } else if (typeof newValue === 'object') {
          const value = (newValue as Record<string, unknown>)?.['value'] ?? '';
          setValue(value as string);
          propsOnChange(value as string);
        }
      },
      [
        getChanges,
        props.attribute.MaxInputLength,
        props.maxInputLength,
        propsOnChange,
        formatter.parser,
        props.attribute.FormatterParseParams,
      ]
    );

    const onEnumChange = useCallback(
      (suggestion: ISuggestion | null) => {
        const newValue = suggestion?.value ?? '';
        setValue(newValue);
        propsOnChange(newValue);
      },
      [propsOnChange]
    );

    const onReset = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();

        if (props.onBeforeClear) {
          props.onBeforeClear();
          return;
        }
        inputRef.current?.focus();
        setValue(props.defaultClearValue || '');
        setErrorMessage('');
        setValidationResult(undefined);
        propsOnChange(props.defaultClearValue || '');
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onBeforeClear, propsOnChange]
    );

    const onBeforeInput = useCallback(
      (event: any) => {
        if (
          !formatter.parser.validateKey(
            event,
            props.attribute.FormatterParseParams,
            event.data
          )
        ) {
          event.preventDefault();
        }
      },
      [formatter.parser, props.attribute.FormatterParseParams]
    );

    if (
      focused &&
      props.calcFocusedChangeMarker &&
      !props.disableChangeMarker
    ) {
      const parseResult = formatter.parser.parse(
        value,
        props.attribute.FormatterParseParams
      );

      if (parseResult.result === ParseResult.OK) {
        marker =
          value !== undefined &&
          !businessDataFormValuesEqual(props.savedValue, parseResult.value);
      }
    }

    return (
      <EditableAttribute
        {...rest}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        baseOnChange={baseOnChange}
        onChange={onChange}
        onEventChange={onEventChange}
        onEnumChange={onEnumChange}
        onReset={onReset}
        focused={focused}
        onFocus={onFocus}
        onBlur={onBlur}
        onBeforeInput={onBeforeInput}
        baseValue={baseValue}
        value={value}
        formatter={formatter}
        validationResult={validationResult}
        attribute={attribute}
        errorMessage={
          props.errorMessage !== undefined
            ? props.errorMessage
            : isParseWarning(validationResult?.result)
            ? ''
            : errorMessage
        }
        warningMessage={
          props.warningMessage !== undefined
            ? props.warningMessage
            : isParseWarning(validationResult?.result)
            ? errorMessage
            : ''
        }
        edited={Boolean(marker)}
        inputRef={inputRef}
      />
    );
  });
EnhancedEditableAttribute.displayName = 'EnhancedEditableAttribute';

export default EnhancedEditableAttribute;
